export type CommerceCatalogRoute =
  | { kind: 'catalogs' }
  | { kind: 'catalog'; catalogId: string }
  | { kind: 'product'; catalogId: string; productId: string };

export type CommerceHelpRoute = {
  kind: 'help';
  sectionId: string;
};

export type CommerceEligibilityRoute =
  | { kind: 'eligibility'; mode: 'list' }
  | { kind: 'eligibility'; mode: 'create' }
  | { kind: 'eligibility'; mode: 'edit'; policyId: string };

export type CommercePricebookRoute =
  | { kind: 'pricebooks'; mode: 'list' }
  | { kind: 'pricebooks'; mode: 'create' }
  | { kind: 'pricebooks'; mode: 'edit'; pricebookId: string };

const OWNER_COMMERCE_ROUTES = new Set([
  '/commerce',
  '/commerce/storefronts',
  '/commerce/publications',
  '/commerce/entitlements',
  '/commerce/linked-resellers',
  '/commerce/channels',
  '/commerce/markets',
  '/commerce/access-points',
  '/commerce/data-setup',
  '/commerce/assignments',
  '/commerce/commission-plans',
  '/commerce/commission-ledger',
  '/commerce/discounts',
  '/commerce/carts',
  '/commerce/quotes',
  '/commerce/orders',
]);

const RESELLER_COMMERCE_ROUTES = new Set([
  '/reseller/entitlements',
  '/reseller/linked-sellers',
  '/agent/quotes',
  '/reseller/carts',
  '/reseller/orders',
  '/reseller/commission-ledger',
  '/sales/quotes',
  '/sales/orders',
]);

function normalizePath(path: string): string {
  const withoutQuery = path.split('?')[0] || '/';
  if (withoutQuery.length > 1 && withoutQuery.endsWith('/')) {
    return withoutQuery.slice(0, -1);
  }
  return withoutQuery;
}

export function parseCommerceCatalogRoute(path: string): CommerceCatalogRoute | null {
  const normalized = normalizePath(path);
  const segments = normalized.split('/').filter(Boolean);

  if (segments[0] !== 'commerce' || segments[1] !== 'catalogs') {
    return null;
  }

  if (segments.length === 2) {
    return { kind: 'catalogs' };
  }

  if (segments.length === 3) {
    return { kind: 'catalog', catalogId: decodeURIComponent(segments[2]) };
  }

  if (segments.length === 5 && segments[3] === 'products') {
    return {
      kind: 'product',
      catalogId: decodeURIComponent(segments[2]),
      productId: decodeURIComponent(segments[4]),
    };
  }

  return null;
}

export function parseCommerceHelpRoute(path: string): CommerceHelpRoute | null {
  const normalized = normalizePath(path);
  const segments = normalized.split('/').filter(Boolean);

  if (segments[0] !== 'commerce' || segments[1] !== 'help') {
    return null;
  }

  if (segments.length === 2) {
    return { kind: 'help', sectionId: 'catalogs' };
  }

  if (segments.length === 3) {
    return { kind: 'help', sectionId: decodeURIComponent(segments[2]) };
  }

  return null;
}

export function parseEligibilityRoute(path: string): CommerceEligibilityRoute | null {
  const normalized = normalizePath(path);
  const segments = normalized.split('/').filter(Boolean);

  if (segments[0] !== 'commerce' || segments[1] !== 'eligibility') {
    return null;
  }

  if (segments.length === 2) {
    return { kind: 'eligibility', mode: 'list' };
  }

  if (segments.length === 3 && segments[2] === 'new') {
    return { kind: 'eligibility', mode: 'create' };
  }

  if (segments.length === 3) {
    return {
      kind: 'eligibility',
      mode: 'edit',
      policyId: decodeURIComponent(segments[2]),
    };
  }

  return null;
}

export function parsePricebookRoute(path: string): CommercePricebookRoute | null {
  const normalized = normalizePath(path);
  const segments = normalized.split('/').filter(Boolean);

  if (segments[0] !== 'commerce' || segments[1] !== 'pricebooks') {
    return null;
  }

  if (segments.length === 2) {
    return { kind: 'pricebooks', mode: 'list' };
  }

  if (segments.length === 3 && segments[2] === 'new') {
    return { kind: 'pricebooks', mode: 'create' };
  }

  if (segments.length === 3) {
    return {
      kind: 'pricebooks',
      mode: 'edit',
      pricebookId: decodeURIComponent(segments[2]),
    };
  }

  return null;
}

export function hasImmersiveCommerceEditorRoute(path: string): boolean {
  const pricebookRoute = parsePricebookRoute(path);
  return pricebookRoute?.mode === 'create' || pricebookRoute?.mode === 'edit';
}

export function buildCatalogDirectoryRoute() {
  return '/commerce/catalogs';
}

export function buildCatalogRoute(catalogId: string) {
  return `/commerce/catalogs/${encodeURIComponent(catalogId)}`;
}

export function buildCatalogProductRoute(catalogId: string, productId: string) {
  return `${buildCatalogRoute(catalogId)}/products/${encodeURIComponent(productId)}`;
}

export function buildPricebooksListRoute() {
  return '/commerce/pricebooks';
}

export function buildPricebookCreateRoute() {
  return '/commerce/pricebooks/new';
}

export function buildPricebookEditorRoute(pricebookId: string) {
  return `${buildPricebooksListRoute()}/${encodeURIComponent(pricebookId)}`;
}

export function isOwnerCommerceRoute(path: string): boolean {
  const normalized = normalizePath(path);
  return (
    OWNER_COMMERCE_ROUTES.has(normalized) ||
    parseCommerceCatalogRoute(normalized) !== null ||
    parseCommerceHelpRoute(normalized) !== null ||
    parseEligibilityRoute(normalized) !== null ||
    parsePricebookRoute(normalized) !== null
  );
}

export function isResellerCommerceRoute(path: string): boolean {
  const normalized = normalizePath(path);
  return RESELLER_COMMERCE_ROUTES.has(normalized);
}
